import { useEffect, useState } from 'react';
import { Loader2, Star, Check, Copy } from 'lucide-react';
import { withTenant } from './BookingPage';

/*
 * "How did we do?" — the page the emailed link opens.
 *
 * ⚠️ ON THE TENANT'S OWN DOMAIN, AND IT LOOKS LIKE HER. The customer has a relationship with a
 * groomer, not with a platform they have never heard of. StartMeUpPro appears nowhere here,
 * the accent colour is hers, and the business name is the first thing on the screen.
 *
 * ⚠️ THE STARS ARE HERE, NOT IN THE EMAIL, AND NOTHING IS RECORDED BY OPENING THIS PAGE. Every
 * write is a POST the customer presses. Spam filters, corporate link scanners and inbox
 * prefetchers follow every URL in a message they are checking; one-tap rating links would have
 * filled the table with five-star reviews nobody typed, published under real customers' names.
 *
 * ⚠️ THE PUBLIC LINK IS OFFERED TO EVERY RESPONDER, WHATEVER THEY SAID. Read the gating note in
 * migration 0050 before "improving" that: routing only happy customers to Google is against
 * Google's own policy and sits inside the FTC's 2024 rule on review suppression, and the
 * profile it risks belongs to the operator, not to us.
 */

interface ReviewPageData {
  businessName: string;
  customerName: string;
  responded: boolean;
  rating: number | null;
  externalReviewUrl: string | null;
  optedOut: boolean;
  accentColor: string | null;
}

const FALLBACK_ACCENT = '#2F6B4F';

/** `/review/<token>` and `/review/<token>/no`, parsed without a router — same as `/book`. */
export function parseReviewPath(pathname: string): { token: string; optOut: boolean } | null {
  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  if (parts[0] !== 'review' || !parts[1]) return null;
  return { token: parts[1], optOut: parts[2] === 'no' };
}

export default function ReviewPage({ token, optOut }: { token: string; optOut: boolean }) {
  const [data, setData] = useState<ReviewPageData | null>(null);
  const [failure, setFailure] = useState<'missing' | 'error' | null>(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<'answered' | 'opted-out' | null>(null);
  const [externalUrl, setExternalUrl] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetch(withTenant(`/api/public/review/${encodeURIComponent(token)}`))
      .then(async (r) => {
        if (!live) return;
        // 404 covers unknown, unsent and another tenant's — one answer for all three, so the
        // page says the same honest thing rather than guessing which.
        if (r.status === 404) return setFailure('missing');
        if (!r.ok) return setFailure('error');
        const d = (await r.json()) as ReviewPageData;
        setData(d);
        setExternalUrl(d.externalReviewUrl);
        if (d.responded) setDone('answered');
        if (d.optedOut) setDone('opted-out');
      })
      .catch(() => live && setFailure('error'));
    return () => {
      live = false;
    };
  }, [token]);

  const accent = data?.accentColor ?? FALLBACK_ACCENT;

  async function submit() {
    if (rating === 0 || busy) return;
    setBusy(true);
    try {
      const res = await fetch(withTenant(`/api/public/review/${encodeURIComponent(token)}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comment: comment.trim() || undefined }),
      });
      /*
       * ⚠️ 409 IS A SUCCESS FROM HERE. It means this link was already answered — a second tab,
       * a double tap, a back button. Showing an error would tell somebody their review did not
       * land when it did, and the likeliest thing they would do next is write it again.
       */
      if (res.ok || res.status === 409) {
        const body = res.ok ? ((await res.json()) as { externalReviewUrl: string | null }) : null;
        if (body) setExternalUrl(body.externalReviewUrl);
        setDone('answered');
      } else {
        setFailure('error');
      }
    } catch {
      setFailure('error');
    } finally {
      setBusy(false);
    }
  }

  async function optOutNow() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(
        withTenant(`/api/public/review/${encodeURIComponent(token)}/optout`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
      );
      if (res.ok) setDone('opted-out');
      else setFailure('error');
    } catch {
      setFailure('error');
    } finally {
      setBusy(false);
    }
  }

  if (failure === 'missing') {
    return (
      <Shell>
        <p className="text-slate-700">This link isn’t valid any more.</p>
        <p className="mt-2 text-sm text-slate-500">
          If you meant to leave a review, get in touch with the business directly — they’ll be
          glad to hear from you.
        </p>
      </Shell>
    );
  }

  if (failure === 'error') {
    return (
      <Shell>
        <p className="text-slate-700">We couldn’t load this just now.</p>
        <p className="mt-2 text-sm text-slate-500">It’s us, not you — try again in a moment.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-5 inline-flex items-center rounded-lg bg-slate-800 px-5 py-2.5 font-semibold text-white transition-colors hover:bg-slate-700"
        >
          Try again
        </button>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell>
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" />
      </Shell>
    );
  }

  if (done === 'opted-out') {
    return (
      <Shell>
        <Badge accent={accent} />
        <h1 className="mt-4 text-xl font-semibold text-slate-900">That’s sorted.</h1>
        <p className="mt-2 text-slate-600">
          {data.businessName} won’t ask you for a review again. This doesn’t change anything else
          — you’ll still get your booking confirmations and invoices.
        </p>
      </Shell>
    );
  }

  /*
   * The opt-out link opens straight into this confirmation rather than acting on arrival. A GET
   * that unsubscribed somebody would let any link scanner do it on their behalf — and the
   * operator would never find out why that customer went quiet.
   *
   * ⚠️ CHECKED BEFORE THE ANSWERED STATE, WHICH IS THE ONLY ORDER THAT WORKS. Both links are in
   * the same email, and leaving a review does not mean wanting to be asked again next time —
   * with the thank-you first, somebody who did both got congratulated instead of unsubscribed,
   * and the one thing they actually asked for silently did not happen.
   */
  if (optOut) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold text-slate-900">
          Stop {data.businessName} asking for reviews?
        </h1>
        <p className="mt-2 text-slate-600">
          You’ll still get booking confirmations and invoices — this only stops the “how did we
          do?” emails.
        </p>
        <button
          type="button"
          onClick={optOutNow}
          disabled={busy}
          className="mt-6 inline-flex items-center justify-center rounded-lg bg-slate-800 px-5 py-2.5 font-semibold text-white transition-colors hover:bg-slate-700 disabled:opacity-60"
        >
          {busy ? 'One moment…' : 'Don’t ask me again'}
        </button>
        <p className="mt-6 text-sm text-slate-500">
          <a href={`/review/${encodeURIComponent(token)}`} className="underline">
            Actually, I’ll leave a review
          </a>
        </p>
      </Shell>
    );
  }

  if (done === 'answered') {
    return (
      <Shell>
        <Badge accent={accent} />
        <h1 className="mt-4 text-xl font-semibold text-slate-900">Thanks — that’s a big help.</h1>
        <p className="mt-2 text-slate-600">{data.businessName} has your feedback.</p>
        {externalUrl && (
          <>
            <p className="mt-6 text-slate-600">
              If you’ve got another minute, posting it publicly helps far more people find them.
            </p>
            {/*
              ⚠️ THE RETYPING IS THE DROP-OFF. Somebody who has just written three sentences about
              their dog is not writing them again into a second box, so "post it publicly" without
              this reads as "start over" — and the review that already exists never makes it to
              the profile that needs it.

              Only ever what they typed a moment ago, held in this page's own state. It is
              deliberately NOT fetched back from the server: this link is reachable by anyone
              holding it — forwarded, screenshotted, left in a shared browser — and the same
              reasoning that keeps the job off this page keeps somebody's words off it too. The
              cost is that a reload loses the button, which is the right way round.
            */}
            {comment.trim() && <CopyMyWords text={comment.trim()} accent={accent} />}
            <a
              href={externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center rounded-lg px-5 py-2.5 font-semibold text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: accent }}
            >
              Post it publicly
            </a>
          </>
        )}
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-xl font-semibold text-slate-900">
        How did {data.businessName} do, {data.customerName.split(' ')[0]}?
      </h1>
      <p className="mt-2 text-slate-600">Tap a star. Twenty seconds, and it genuinely helps.</p>

      {/*
        ⚠️ RADIO SEMANTICS, NOT FIVE BUTTONS. This is one choice out of five, and it is the whole
        point of the page — a screen reader announcing "button, button, button" gives somebody no
        way to know what they picked or that they picked anything.
      */}
      <div
        className="mt-6 flex justify-center gap-2"
        role="radiogroup"
        aria-label={`How did ${data.businessName} do?`}
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={rating === n}
            aria-label={`${n} ${n === 1 ? 'star' : 'stars'}`}
            onClick={() => setRating(n)}
            className="rounded-lg p-1 transition-transform hover:scale-110"
          >
            <Star
              className="h-10 w-10"
              /*
               * ⚠️ FILLED, NOT JUST COLOURED. An outline star tinted with the accent reads as
               * "unselected" to anyone who cannot compare it side by side with its neighbour —
               * and on a low-contrast accent it disappears entirely.
               */
              fill={n <= rating ? accent : 'none'}
              stroke={n <= rating ? accent : '#94a3b8'}
            />
          </button>
        ))}
      </div>

      <label className="mt-6 block text-left">
        <span className="text-sm font-medium text-slate-700">
          Anything you’d like to add? <span className="font-normal text-slate-400">Optional</span>
        </span>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, 1500))}
          rows={4}
          placeholder="What went well, or what could be better…"
          className="mt-2 w-full rounded-lg border border-slate-300 p-3 text-slate-900 outline-none focus:border-slate-400"
        />
      </label>

      <button
        type="button"
        onClick={submit}
        disabled={rating === 0 || busy}
        className="mt-4 w-full rounded-lg px-5 py-3 font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
        style={{ backgroundColor: accent }}
      >
        {busy ? 'Sending…' : 'Send it'}
      </button>

      <p className="mt-6 text-sm text-slate-500">
        <a href={`/review/${encodeURIComponent(token)}/no`} className="underline">
          Don’t ask me again
        </a>
      </p>
    </Shell>
  );
}

/**
 * Their own words, and one press to carry them to the public profile.
 *
 * ⚠️ THE TEXT IS ON THE PAGE, NOT ONLY ON THE CLIPBOARD. Every route to the clipboard can fail
 * — an iOS browser that wants a tighter gesture, a locked-down work device, a permissions
 * prompt nobody grants — and all of them fail SILENTLY, leaving a button that says nothing
 * happened. Shown and selectable, the worst case is a manual select-and-copy rather than a
 * customer who thinks they copied something and pastes whatever was there before.
 */
function CopyMyWords({ text, accent }: { text: string; accent: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    let ok = false;
    try {
      // Needs a secure context; this page is only ever served over https.
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      /*
       * The old way, still the only way in a few places. A textarea off-screen rather than
       * hidden: `display:none` cannot hold a selection, so the copy silently does nothing.
       */
      try {
        const el = document.createElement('textarea');
        el.value = text;
        el.setAttribute('readonly', '');
        el.style.position = 'fixed';
        el.style.top = '-1000px';
        document.body.appendChild(el);
        el.select();
        ok = document.execCommand('copy');
        document.body.removeChild(el);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      // Long enough to read, short enough that a second press still looks live.
      window.setTimeout(() => setCopied(false), 6000);
    }
  }

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-left">
      <p className="whitespace-pre-wrap text-sm text-slate-700">{text}</p>
      <button
        type="button"
        onClick={copy}
        className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100"
      >
        {copied ? (
          <Check className="h-4 w-4" style={{ color: accent }} />
        ) : (
          <Copy className="h-4 w-4" />
        )}
        {copied ? 'Copied' : 'Copy what I wrote'}
      </button>
      {/*
        Announced, not just recoloured — the check mark beside a button is invisible to anyone
        using a screen reader, and "did that work?" is the whole question at this step.
      */}
      <p aria-live="polite" className="mt-2 text-sm text-slate-500">
        {copied ? 'Paste it into the box on the next page.' : '\u00a0'}
      </p>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white px-6 py-14">
      <div className="mx-auto max-w-md text-center">{children}</div>
    </div>
  );
}

function Badge({ accent }: { accent: string }) {
  return (
    <div
      className="mx-auto grid h-12 w-12 place-items-center rounded-full"
      style={{ backgroundColor: `${accent}1a` }}
    >
      <Check className="h-6 w-6" style={{ color: accent }} />
    </div>
  );
}
